import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import api from "@/lib/api";
import { annotateRegIndent } from "@/lib/regIndent";
import { linkifyEccns } from "@/lib/linkifyRefs";
import { ArrowLeft, ArrowRight, ArrowLeftCircle, ExternalLink, AlertTriangle } from "lucide-react";

export default function USMLParagraph() {
  const { roman, letter } = useParams();
  const romanUpper = String(roman || "").toUpperCase();
  const letterLower = String(letter || "").toLowerCase();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await api.get(`/usml/paragraphs/${encodeURIComponent(romanUpper)}/${encodeURIComponent(letterLower)}`);
        if (!cancelled) setData(r.data);
      } catch { if (!cancelled) setNotFound(true); }
    })();
    // Reset scroll for a fresh paragraph.
    window.scrollTo({ top: 0, behavior: "auto" });
    return () => { cancelled = true; };
  }, [romanUpper, letterLower]);

  const contentHtml = useMemo(() => {
    if (!data?.paragraph?.content_html) return "";
    // Cross-navigator link to CCL — open in new tab so the USML reader
    // keeps their place.
    return linkifyEccns(data.paragraph.content_html, { newTab: true });
  }, [data]);

  // After the rendered HTML is inserted, walk paragraphs and assign
  // data-lvl for the indent guides. Seed with the parent letter so the
  // opening "(a)" line is treated as level 0 and every later lowercase
  // marker resolves as a roman-numeral sub-paragraph.
  useEffect(() => {
    if (!contentHtml) return;
    const el = document.querySelector('[data-testid="usml-para-content"]');
    if (el) annotateRegIndent(el, { parentLetter: data?.paragraph?.letter });
  }, [contentHtml, data]);

  if (notFound) return <Navigate to={`/usml/${romanUpper}`} replace />;

  const paragraph = data?.paragraph;
  const category = data?.category;
  const prev = data?.prev_letter;
  const next = data?.next_letter;

  const lastMod = paragraph?.updated_at
    ? new Date(paragraph.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const sourceAsOf = category?.source_as_of || paragraph?.source_as_of || "—";

  return (
    <Layout>
      <SEO
        title={paragraph ? `USML ${romanUpper}(${paragraph.letter}) — ${paragraph.heading}` : `USML ${romanUpper}(${letterLower})`}
        description={paragraph ? `Full text of paragraph (${paragraph.letter}) in Category ${romanUpper} of the U.S. Munitions List: ${paragraph.heading}. Mirrored from eCFR.` : ""}
        path={`/usml/e/${romanUpper}/${letterLower}`}
      />

      {/* Persistent disclaimer banner — top */}
      <div className="bg-brass-deep/10 border-b border-brass/40" data-testid="usml-para-banner-top">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 text-[12px] leading-snug">
          <AlertTriangle className="w-4 h-4 text-brass-deep shrink-0" strokeWidth={2} />
          <p className="text-ink">
            <strong className="uppercase tracking-wide text-[10px]">Mirrored from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}.</strong>{" "}
            This page is a navigational aid, not the authoritative text and not legal advice. Verify against the eCFR.
          </p>
          {paragraph?.source_url && (
            <a
              href={paragraph.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-ink border-b border-brass/60 hover:text-brass hover:border-brass transition-colors whitespace-nowrap"
              data-testid="usml-para-source-top"
            >
              View on eCFR <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </a>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="usml-para-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-14 pb-10">
          <Link
            to={`/usml/${romanUpper}`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors"
            data-testid="usml-para-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
            {category ? `Category ${romanUpper} — ${category.title}` : `Category ${romanUpper}`}
          </Link>
          <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-3">
            <span className="font-mono text-4xl sm:text-5xl font-semibold text-brass-deep" data-testid="usml-para-code">
              {romanUpper}({paragraph?.letter || letterLower})
            </span>
            <Eyebrow>USML Paragraph</Eyebrow>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl text-ink leading-snug tracking-tight mt-4 max-w-3xl" data-testid="usml-para-heading">
            {paragraph?.heading || "Loading…"}
          </h1>
          {paragraph && (
            <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-ink-soft" data-testid="usml-para-last-modified">
              Last modified {lastMod}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12" data-testid="usml-para-body">
        {!paragraph && <p className="text-ink-soft">Loading paragraph text…</p>}
        {paragraph && contentHtml && (
          <div
            className="ccl-entry-content usml-content reg-indent"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
            data-testid="usml-para-content"
          />
        )}
        {paragraph && !contentHtml && (
          <p className="text-ink-soft italic" data-testid="usml-para-content">[Reserved]</p>
        )}
      </section>

      {/* Prev / Next paragraph nav */}
      {(prev || next) && (
        <section className="border-t border-hairline bg-ivory-alt" data-testid="usml-para-nav">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between gap-4">
            {prev ? (
              <Link
                to={`/usml/e/${romanUpper}/${prev}`}
                className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-ink hover:text-brass transition-colors"
                data-testid="usml-para-prev"
              >
                <ArrowLeftCircle className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline">Previous:</span>
                <span className="font-mono font-semibold">({prev})</span>
              </Link>
            ) : <span />}
            <Link
              to={`/usml/${romanUpper}`}
              className="text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors"
              data-testid="usml-para-all"
            >
              All paragraphs
            </Link>
            {next ? (
              <Link
                to={`/usml/e/${romanUpper}/${next}`}
                className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-ink hover:text-brass transition-colors"
                data-testid="usml-para-next"
              >
                <span className="hidden sm:inline">Next:</span>
                <span className="font-mono font-semibold">({next})</span>
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            ) : <span />}
          </div>
        </section>
      )}

      {/* Bottom disclaimer */}
      <section className="bg-ink text-ivory" data-testid="usml-para-banner-bottom">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Verify Before Relying</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This is a mirror of USML {romanUpper}({paragraph?.letter || letterLower}) from eCFR{sourceAsOf !== "—" ? ` as of ${sourceAsOf}` : ""}. Nothing on this page is legal advice.
          </p>
          {paragraph?.source_url && (
            <a
              href={paragraph.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 bg-ivory text-ink px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass hover:text-ivory transition-colors"
              data-testid="usml-para-source-bottom"
            >
              View {romanUpper}({paragraph?.letter || letterLower}) on eCFR
              <ExternalLink className="w-4 h-4" strokeWidth={2} />
            </a>
          )}
        </div>
      </section>
    </Layout>
  );
}
